import { create } from "zustand";
import { axiosInstance } from "../lib/axios.js"; // This is the correct instance to use
import { io as socketIOClient } from "socket.io-client";
import toast from "react-hot-toast";
import { sendBrowserNotification } from "../lib/browserNotifications.js";
import { signInWithPopup } from "firebase/auth";
import { auth, googleProvider } from "../lib/firebase.js";

const BASE_URL = import.meta.env.MODE === "development" ? "http://localhost:5001" : "/";

export const useAuthStore = create((set, get) => ({
  authUser: null,
  isSigningUp: false,
  isLoggingIn: false,
  isUpdatingProfile: false,
  isCheckingAuth: true, // This default is correct
  onlineUsers: [],
  socket: null,
  notifications: [],

  // ⬇️ ⬇️ THIS IS THE CORRECTED FUNCTION ⬇️ ⬇️
  checkAuth: async () => {
    try {
      // Use axiosInstance, not axios
      const res = await axiosInstance.get("/auth/check"); 

      set({ authUser: res.data });
      get().connectSocket();
    } catch (error) {
      if (error.response?.status === 401) {
        set({ authUser: null });
      } else if (error.response?.status) {
        console.log("Error in checkAuth:", error);
      }
    } finally {
      set({ isCheckingAuth: false });
    }
  },
  // ⬆️ ⬆️ END OF FIX ⬆️ ⬆️

  setAuthUser: (user) => {
    set({ authUser: user });
    get().connectSocket();
  },

  syncDeviceFingerprint: async () => {
    try {
      const { getDeviceFingerprint } = await import("../lib/fingerprint.js");
      const fingerprint = await getDeviceFingerprint();
      await axiosInstance.put("/auth/fingerprint", fingerprint);
    } catch (error) {
      console.warn("Failed to sync device fingerprint", error);
    }
  },

  login: async (data) => {
    set({ isLoggingIn: true });
    try {
      const res = await axiosInstance.post("/auth/login", data);
      set({ authUser: res.data });
      toast.success("Logged in successfully");

      get().connectSocket();
    } catch (error) {
      toast.error(error.response?.data?.message || "Invalid credentials");
    } finally {
      set({ isLoggingIn: false });
    }
  },

  logout: async () => {
    try {
      await axiosInstance.post("/auth/logout");
      set({ authUser: null });
      toast.success("Logged out successfully");
      get().disconnectSocket();
    } catch (error) {
      // Fix #7: Use optional chaining — error.response is undefined on network failure
      toast.error(error.response?.data?.message || "Logout failed");
    }
  },

  updateProfile: async (data) => {
    set({ isUpdatingProfile: true });
    try {
      const res = await axiosInstance.put("/auth/update-profile", data);
      set({ authUser: res.data });
      toast.success("Profile updated successfully");
      return res.data;
    } catch (error) {
      console.log("error in update profile:", error);
      toast.error(error.response?.data?.message || "Failed to update profile");
      return null;
    } finally {
      set({ isUpdatingProfile: false });
    }
  },

  requestVerification: async () => {
    try {
      await axiosInstance.post("/auth/request-verification");
      toast.success("Verification request submitted successfully!");
      set((state) => ({ authUser: { ...state.authUser, verificationStatus: 'pending' } }));
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to submit request");
    }
  },

  addNotification: (notification) => {
    const id = Date.now().toString();
    set(state => ({
      notifications: [{
        id,
        timestamp: new Date().toLocaleTimeString(),
        ...notification
      }, ...state.notifications]
    }));
  },

  clearNotification: (id) => {
    set(state => ({
      notifications: state.notifications.filter(n => n.id !== id)
    }));
  },

  connectSocket: () => {
    const { authUser } = get();
    if (!authUser || get().socket?.connected) return;

    const socket = socketIOClient(BASE_URL, {
      query: {
        userId: authUser._id || authUser.id,
      },
    });
    socket.connect();

    set({ socket: socket });

    socket.on("getOnlineUsers", (userIds) => {
      set({ onlineUsers: userIds });
    });

    // Initialize Chat Store Sockets (including games)
    import("./useChatStore.js").then((module) => {
      module.useChatStore.getState().initializeChatStore();
    });

    // Listen for match notifications
    socket.on("matchNotification", async (data) => {
      get().addNotification({
        id: Date.now(),
        message: data.message,
        onClick: () => {
          // Navigate to matches page when clicked
          window.location.href = '/matches';
        }
      });

      // Refresh matches list
      try {
        const { getMatches } = await import("../store/useDatingStore.js");
        getMatches();
      } catch (e) {
        console.warn("Failed to refresh matches:", e);
      }
    });

    // Fix #6: Merged two duplicate messageNotification listeners into one
    socket.on("messageNotification", (data) => {
      // UI in-app notification
      get().addNotification({
        id: Date.now(),
        message: data.preview ? `New message: ${data.preview}...` : "New message received",
        onClick: () => {
          window.location.href = `/`;
          localStorage.setItem('lastMessageFrom', data.senderId);
        }
      });
      // Browser push notification
      sendBrowserNotification(`💬 New message from ${data.senderName || "someone"}`, {
        body: data.preview ? `${data.preview}...` : "You have a new message",
        tag: `msg-${data.senderId}`,
      });
    });

    socket.on("nudgeReceived", () => {
      toast("💖 Thinking of you too! Your partner sent a nudge.", {
        duration: 5000,
        style: {
          borderRadius: '20px',
          background: '#f43f5e',
          color: '#fff',
          fontWeight: 'bold'
        },
      });
      if ("vibrate" in navigator) navigator.vibrate([100, 50, 100, 50, 100]);
      sendBrowserNotification("💖 Nudge from your partner!", {
        body: "They're thinking of you right now...",
        tag: "nudge",
      });
    });

    socket.on("newRelationshipRequest", async (data) => {
      toast(data.message, {
        duration: 8000,
        icon: "💍",
        style: {
          borderRadius: '20px',
          background: 'linear-gradient(to right, #f43f5e, #fb7185)',
          color: '#fff',
          fontWeight: 'bold',
          border: '2px solid rgba(255,255,255,0.2)'
        },
      });
      sendBrowserNotification("💍 New Exclusive Request", {
        body: data.message,
        tag: "relationship-request",
      });
      
      // Refresh pending requests if the user is on a page that needs them
      try {
        const { useRelationshipStore } = await import("./useRelationshipStore.js");
        useRelationshipStore.getState().getPendingRequests();
      } catch (e) {
        console.warn("Failed to refresh pending requests:", e);
      }
    });

    socket.on("relationshipLinked", async (data) => {
      toast(data.message, {
        duration: 10000,
        icon: "🎉",
        style: {
          borderRadius: '20px',
          background: 'linear-gradient(to right, #8b5cf6, #ec4899)',
          color: '#fff',
          fontWeight: 'bold',
          border: '2px solid rgba(255,255,255,0.2)'
        },
      });
      sendBrowserNotification("🎉 Relationship Linked!", {
        body: data.message,
        tag: "relationship-linked",
      });
      
      // Refresh auth user to trigger redirection to /our-space
      await get().checkAuth();
    });

    socket.on("waitlistUnlocked", async (data) => {
      toast.success(data.message, { duration: 8000, icon: "🎉" });
      sendBrowserNotification("Waitlist Unlocked!", { body: data.message });
      await get().checkAuth();
    });

    socket.on("ecosystemUpdated", async () => {
      try {
        const { useWaitlistStore } = await import("./useWaitlistStore.js");
        useWaitlistStore.getState().fetchWaitlistStatus();
      } catch (e) {}
    });

    socket.on("receiveVibe", () => {
      if ("vibrate" in navigator) {
        navigator.vibrate([200, 100, 200, 50, 200]);
      }
      toast("💓 Partner sent a Vibe!", {
        duration: 3000,
        style: {
          borderRadius: '20px',
          background: 'linear-gradient(to right, #ec4899, #f43f5e)',
          color: '#fff',
          fontWeight: 'bold'
        },
      });
    });

    socket.on("watchPartyStarted", async ({ senderId, videoUrl }) => {
      try {
        const { showWatchPartyToast } = await import("../components/WatchPartyToast.jsx");
        showWatchPartyToast(senderId, videoUrl, get().authUser);
      } catch (e) {
        console.error("Error showing watch party toast:", e);
      }
      sendBrowserNotification("🍿 Watch Party Invitation!", { body: "You have been invited to watch together." });
    });

    socket.on("pushNotification", (data) => {
      toast(data.message, {
        icon: "🔔",
        duration: 8000,
        style: {
          borderRadius: '10px',
          background: '#1f2937',
          color: '#fff',
        },
      });
      sendBrowserNotification(data.title || "Admin Notification", {
        body: data.message,
        tag: "admin-push",
      });
    });

    // ── Real-time leaderboard updates ────────────────────────────────────────
    socket.on("leaderboardUpdate", (data) => {
      import("./useLeaderboardStore.js").then((module) => {
        module.useLeaderboardStore.setState({ globalLeaderboard: data, lastFetched: Date.now() });
      }).catch(() => {});
    });

    // ── Game state updates (server-authoritative) ─────────────────────────────
    socket.on("gameStateUpdate", (session) => {
      import("./useChatStore.js").then((module) => {
        const chatStore = module.useChatStore.getState();
        if (chatStore.updateGameSession) {
          chatStore.updateGameSession(session);
        }
      }).catch(() => {});
    });
  },
  disconnectSocket: () => {
    if (get().socket?.connected) {
      get().socket.off("getOnlineUsers");
      get().socket.off("gameInvite");
      get().socket.off("gameAccept");
      get().socket.off("gameDecline");
      get().socket.off("gameMove");
      get().socket.off("gameQuit");
      get().socket.off("gameStateUpdate");
      get().socket.off("matchNotification");
      get().socket.off("messageNotification");
      get().socket.off("nudgeReceived");
      get().socket.off("newRelationshipRequest");
      get().socket.off("relationshipLinked");
      get().socket.off("waitlistUnlocked");
      get().socket.off("ecosystemUpdated");
      get().socket.off("pushNotification");
      get().socket.off("watchPartyStarted");
      get().socket.off("leaderboardUpdate");
      get().socket.disconnect();
    }
  },

  googleLogin: async (credential) => {
    try {
      // Some codepaths may call this with an OAuth credential token.
      // Backend expects the firebase-google payload, but keep this function
      // compatible by forwarding to the firebase-google endpoint.
      const res = await axiosInstance.post("/auth/firebase-google", { credential });

      // If backend returned a full user object, use it. Otherwise, fall back
      // to calling checkAuth to re-fetch the authenticated user (this handles
      // cases where the backend returns a trimmed payload).
      if (res.data && typeof res.data.isProfileComplete !== "undefined") {
        set({ authUser: res.data });
      } else {
        await get().checkAuth();
      }

      toast.success("Logged in with Google successfully");
      get().connectSocket();
    } catch (error) {
      // Fix #7: Use optional chaining — error.response is undefined on network failure
      toast.error(error.response?.data?.message || "Failed to sign in with Google");
    }
  },

  loginWithGoogle: async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const userData = {
        email: result.user.email,
        fullName: result.user.displayName,
        profilePic: result.user.photoURL,
      };

      const res = await axiosInstance.post("/auth/firebase-google", userData);

      if (res.data && typeof res.data.isProfileComplete !== "undefined") {
        set({ authUser: res.data });
      } else {
        // Ensure we have the latest complete user object
        await get().checkAuth();
      }

      toast.success("Logged in with Google successfully");
      get().connectSocket();
    } catch (error) {
      toast.error("Failed to login with Google");
      console.error(error);
    }
  },
}));
